/**
 * Minimal shape needed to decide premium access. Both the app `User` model
 * and a raw Firestore document satisfy this, so callers can pass either.
 */
export interface PremiumAccessUserData {
  isSubscribed?: boolean;
  subscriptionStatus?: string | null;
  isTrialActive?: boolean;
  trialEndDate?: string | null;
  /** Peer-referral reward window ("I passed" loop) — ISO string; full access while > now. */
  bonusAccessUntil?: string | null;
  /** One-time "access until you pass" purchase (Phase 2) — non-expiring; server-set. */
  hasLifetimeAccess?: boolean;
  /**
   * Server-computed authoritative decision (from getUserProfile). When present
   * this is trusted directly; there is no client-side re-derivation fallback.
   */
  hasFullAccess?: boolean;
}

/**
 * SINGLE SOURCE OF TRUTH for "can this user access paid content".
 *
 * The backend `getUserProfile` computes the authoritative `hasFullAccess` (an
 * ACTIVE subscription OR a CURRENTLY-VALID trial — an EXPIRED trial does NOT
 * grant access). This helper simply TRUSTS that server-computed flag.
 *
 * We deliberately do NOT re-derive the access rule on the client: a raw
 * Firestore doc with no server-computed flag returns `false`. That divergent
 * client copy previously drifted from the backend and leaked access to expired
 * trials. Callers must read the computed `hasFullAccess` from getUserProfile
 * rather than a raw user doc.
 */
export const hasFullAccess = (
  userData: PremiumAccessUserData | null | undefined
): boolean => {
  if (!userData) return false;

  // AUTHORITATIVE PATH: trust the server-computed decision from getUserProfile.
  if (typeof userData.hasFullAccess === 'boolean') {
    return userData.hasFullAccess;
  }

  // FALLBACK (raw Firestore doc with no server-computed flag): no access.
  return false;
};

/**
 * Paid for it: the one-time purchase or an active subscription. Narrower than
 * `hasFullAccess`, which is also true during a trial or referral bonus, when the
 * $129 offer is exactly what they still need to see.
 */
export const hasPaidAccess = (userData: PremiumAccessUserData | null | undefined): boolean => {
  const sub = userData?.subscriptionStatus;
  return userData?.hasLifetimeAccess === true || sub === 'active' || (typeof sub === 'string' && sub.startsWith('active_'));
};
